/**
 * SIGGO — slider de movil generico.
 *
 * Convierte una pila de bloques en un carrusel horizontal SOLO por debajo del
 * punto de corte de movil. El desplazamiento lo hace `scroll-snap` en CSS;
 * este script solo mueve el scroller al pulsar las flechas, apaga la flecha
 * que no lleva a ninguna parte y coloca las flechas a la altura de la foto.
 *
 * Marcado esperado:
 *   <div data-siggo-ms>
 *     <div data-siggo-ms-track> ...hijos, uno por diapositiva... </div>
 *     <button data-siggo-ms-prev> <button data-siggo-ms-next>
 *   </div>
 *
 * Opcional en el contenedor:
 *   data-siggo-ms-media="<selector>"  elemento cuyo alto centra las flechas.
 */
(() => {
  const MQ = '(max-width: 749px)';
  const mq = window.matchMedia(MQ);

  class MobileSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-siggo-ms-track]');
      this.prev = root.querySelector('[data-siggo-ms-prev]');
      this.next = root.querySelector('[data-siggo-ms-next]');
      if (!this.track) return;

      this.mediaSel = root.dataset.siggoMsMedia || '';

      this.onScroll = this.#throttle(() => this.sync());
      this.onResize = this.#throttle(() => {
        this.placeArrows();
        this.sync();
      });

      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('pageshow', () => {
        this.placeArrows();
        this.sync();
      });

      // Las fotos entran tarde: cuando cargan cambia el alto y hay que
      // recolocar las flechas.
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', this.onResize, { once: true });
        }
      });

      this.placeArrows();
      this.sync();
    }

    get slides() {
      return Array.from(this.track.children);
    }

    /** Ancho de una diapositiva incluyendo el hueco entre ellas. */
    get stride() {
      const first = this.slides[0];
      if (!first) return this.track.clientWidth;
      const second = this.slides[1];
      if (second) {
        const d = second.getBoundingClientRect().left - first.getBoundingClientRect().left;
        if (d > 1) return d;
      }
      return first.getBoundingClientRect().width;
    }

    step(dir) {
      this.track.scrollBy({ left: this.stride * dir, behavior: 'smooth' });
    }

    /**
     * Centra las flechas sobre la foto en vez de sobre el bloque entero: el
     * bloque incluye titulo y precio, asi que su centro cae demasiado abajo.
     */
    placeArrows() {
      if (!this.mediaSel) return;
      const media = this.track.querySelector(this.mediaSel);
      if (!media) return;
      const h = media.getBoundingClientRect().height;
      if (h > 0) this.root.style.setProperty('--siggo-ms-arrow-top', `${Math.round(h / 2)}px`);
    }

    sync() {
      const activo = mq.matches;
      const varias = this.slides.length > 1;
      this.root.toggleAttribute('data-siggo-ms-on', activo && varias);
      if (!activo || !varias) return;

      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.toggleAttribute('disabled', x <= 1);
      if (this.next) this.next.toggleAttribute('disabled', x >= max - 1);
    }

    #throttle(fn) {
      let ticking = false;
      return () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          fn();
        });
      };
    }
  }

  const instancias = [];

  function init() {
    document.querySelectorAll('[data-siggo-ms]').forEach((root) => {
      if (root.dataset.siggoMsReady) return;
      root.dataset.siggoMsReady = '1';
      instancias.push(new MobileSlider(root));
    });
  }

  const refrescar = () => instancias.forEach((s) => {
    s.placeArrows();
    s.sync();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Las secciones diferidas y el editor de temas inyectan marcado despues.
  document.addEventListener('shopify:section:load', () => {
    init();
    refrescar();
  });
  if (mq.addEventListener) mq.addEventListener('change', refrescar);
})();
